import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchBillingProfile } from '../api/billingApi';

const profileListeners = new Set();

/**
 * Be notified of every profile loaded or mutated through ``useBillingProfile``.
 * Lets other surfaces (the "Upgrade to Pro" nav prompt) follow the Plans &
 * Billing tab without re-fetching. Returns the unsubscribe function.
 */
export function subscribeToBillingProfile(listener) {
    profileListeners.add(listener);
    return () => {
        profileListeners.delete(listener);
    };
}

const publishBillingProfile = (profile) => {
    profileListeners.forEach((listener) => listener(profile));
};

/**
 * Load ``GET billing/profile`` on mount and expose the result.
 *
 * ``setProfile`` lets mutations that already return the fresh profile
 * (attach / redeem / cancel …) update the view without a second round trip;
 * ``refresh`` re-fetches, e.g. from the error banner's Retry button.
 */
export function useBillingProfile() {
    const [profile, setProfileState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const mountedRef = useRef(true);

    const setProfile = useCallback((next) => {
        setProfileState(next);
        publishBillingProfile(next);
    }, []);

    const refresh = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const next = await fetchBillingProfile();
            if (mountedRef.current) setProfile(next);
        } catch (err) {
            if (mountedRef.current) setError(err);
        } finally {
            if (mountedRef.current) setLoading(false);
        }
    }, [setProfile]);

    useEffect(() => {
        mountedRef.current = true;
        refresh();
        return () => {
            mountedRef.current = false;
        };
    }, [refresh]);

    return { profile, loading, error, refresh, setProfile };
}

export default useBillingProfile;
